import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import { selectAdminToken } from "../../admin/sessionSlice";
import { processQuestions } from "../../admin/questionSlice";
import { wmlGetState } from "../../admin/functions/wmlApi";
import "./selectCharacter.css";

const WML_MAX_PLAYERS = 30;

export default function SelectCharacter() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentTheme, data: themeData } = useSelector((s) => s.theme);
  const { user } = useSelector((s) => s.auth);
  const adminToken = useSelector(selectAdminToken);
  const [selected, setSelected] = useState(null);
  const [guardLoading, setGuardLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const initRequestedRef = useRef(false);
  const guardCheckedRef = useRef(false);

  const avatarOptions = useMemo(() => {
    const s3Base = process.env.REACT_APP_S3_PATH || "";
    const keys = Array.isArray(themeData?.wmlAvatarKeys) ? themeData.wmlAvatarKeys : [];
    return keys
      .map((filename, index) => {
        const value = String(filename ?? "").trim();
        if (!value) return null;
        return {
          src: `${s3Base}${value}`,
          avatarIndex: index,
        };
      })
      .filter(Boolean);
  }, [themeData?.wmlAvatarKeys]
  );

  useEffect(() => {
    if (selected === null) return;
    if (selected >= avatarOptions.length) {
      setSelected(null);
    }
  }, [selected, avatarOptions.length]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    if (!themeData || !user || !adminToken) return;
    initRequestedRef.current = true;

    dispatch(processQuestions())
      .unwrap()
      .then(() =>
        initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        })
      )
      .catch((err) => {
        console.error("Error in process->init chain:", err);
      });
  }, [dispatch, themeData, user, adminToken]);

  const getRosterSnapshot = async () => {
    if (!adminToken || !currentTheme) return [];
    const data = await wmlGetState({
      token: adminToken,
      currentTheme,
    });
    return Array.isArray(data?.roster) ? data.roster : [];
  };

  const findExistingPlayer = async (rosterOverride) => {
    if (!adminToken || !currentTheme || !user) return null;
    const roster = Array.isArray(rosterOverride)
      ? rosterOverride
      : await getRosterSnapshot();
    const uid = String(user.userId ?? user.id ?? "").trim();
    if (uid) {
      const byUserId = roster.find((p) => String(p.user_id ?? "").trim() === uid);
      if (byUserId) return byUserId;
    }
    const normalizedName = String(user.name || "")
      .trim()
      .toLowerCase();
    if (!normalizedName) return null;
    return (
      roster.find(
        (p) =>
          String(p.display_name || "")
            .trim()
            .toLowerCase() === normalizedName
      ) || null
    );
  };

  useEffect(() => {
    if (guardCheckedRef.current) return;
    if (!adminToken || !currentTheme || !user) return;
    let cancelled = false;
    guardCheckedRef.current = true;
    setGuardLoading(true);

    (async () => {
      try {
        const existing = await findExistingPlayer();
        if (!cancelled && existing) {
          navigate("/who-most-likely", { replace: true });
          return;
        }
      } catch {
        // If guard check fails, allow selection page to remain usable.
      } finally {
        if (!cancelled) {
          setGuardLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, currentTheme, user, navigate]);

  const handleSubmit = async () => {
    if (selected === null) return;
    const picked = avatarOptions[selected];
    if (!picked) return;
    if (submitLoading) return;
    setSubmitLoading(true);
    try {
      const roster = await getRosterSnapshot();
      const existing = await findExistingPlayer(roster);
      if (existing) {
        navigate("/who-most-likely", { replace: true });
        return;
      }
      if (roster.length >= WML_MAX_PLAYERS) {
        await Swal.fire({
          icon: "info",
          title: "This room is full and already buzzing 😄",
          html: [
            "30 players are in and the fun has started",
            "Hop into another session and keep the chaos going there",
          ].join("<br/>"),
          confirmButtonText: "Got it",
        });
        return;
      }
    } catch {
      // If check fails here, continue with selection flow.
    } finally {
      setSubmitLoading(false);
    }
    navigate("/who-most-likely", {
      state: { avatarIndex: picked.avatarIndex },
    });
  };

  return (
    <div className="select-character-page">
      <h1 className="select-character-title">Select your Character</h1>
      <div className="select-character-grid">
        {avatarOptions.length > 0 ? (
          avatarOptions.map((avatar, index) => (
            <button
              key={avatar.avatarIndex}
              type="button"
              className={`select-character-tile${
                selected === index ? " select-character-tile--selected" : ""
              }`}
              onClick={() => setSelected(index)}
              aria-pressed={selected === index}
            >
              <img src={avatar.src} alt="" />
            </button>
          ))
        ) : (
          <p>No characters available for this theme.</p>
        )}
      </div>
      <div className="select-character-footer">
        <button
          type="button"
          className="select-character-submit"
          onClick={handleSubmit}
          disabled={
            selected === null ||
            avatarOptions.length === 0 ||
            submitLoading ||
            guardLoading
          }
        >
          Submit
        </button>
      </div>
    </div>
  );
}
